import { NativeBackButton } from '@/components/ux/NativeBackButton';
import Logo from '../../components/Logo';
import { ManageCookiesLink } from "@/components/consent/ManageCookiesLink";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white dark:bg-neutral-900 py-16 px-4 text-type-900 dark:text-white">
      <div className="max-w-4xl mx-auto">
        <NativeBackButton className="mb-4" />
        <div className="mb-8">
          <Logo link="/" />
        </div>
        
        <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-sm text-dark-muted mb-8">
            <strong>Effective Date:</strong> 6/21/2026
          </p>

          <p className="mb-6">
            Outclose ("Company," "we," "our," or "us") is operated and owned by GBH Holdings Inc., 
            a Delaware limited liability company. We are committed to protecting your privacy and ensuring transparency 
            regarding how we collect, use, and safeguard your personal information. This Privacy Policy explains the 
            types of information we collect, how we use it, with whom we share it, and the rights and choices you have 
            regarding your data.
          </p>

          <p className="mb-8">
            By using our services, websites, or mobile applications (collectively, the "Services"), you consent to 
            the practices described in this Privacy Policy.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">1. Information We Collect</h2>
          <p className="mb-4">We collect the following categories of information:</p>
          
          <h3 className="text-xl font-semibold mt-6 mb-3">a. Information You Provide</h3>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>
              <strong>Account Information:</strong> Name, email address, phone number, gender identity, age group, 
              and any information you voluntarily provide when creating an account.
            </li>
            <li>
              <strong>Profile & Preferences:</strong> Responses to questionnaires, interests, community preferences, 
              and social identifiers you choose to disclose.
            </li>
            <li>
              <strong>Payment Data:</strong> Billing address, payment method details (processed securely via 
              third-party providers such as Stripe or equivalent).
            </li>
            <li>
              <strong>Communications:</strong> Messages, inquiries, or feedback you send to us, including
              interactions with support teams.
            </li>
            <li>
              <strong>Community Content &amp; Connections:</strong> Content you create or share through community
              features—such as event RSVPs, "Kiki" posts and applications, and messages to other users—along with
              any details you choose to share with them (for example, your Instagram handle or interests). If you
              subscribe to our events newsletter, we also collect the email address and preferences you provide.
            </li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">b. Information We Collect Automatically</h3>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>
              <strong>Device Data:</strong> IP address, browser type, operating system, unique device identifiers.
            </li>
            <li>
              <strong>Usage Data:</strong> Log files, clickstream data, app interactions, crash reports, 
              date/time stamps, and referral URLs.
            </li>
            <li>
              <strong>Geolocation Data:</strong> Approximate or precise location information (with your consent), 
              used to match you with relevant events or hosts.
            </li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">c. Information from Third Parties</h3>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>
              <strong>Authentication Providers:</strong> If you sign up using Google, Apple, or other OAuth providers, 
              we receive your basic profile information.
            </li>
            <li>
              <strong>Service Partners:</strong> We may collect limited information from event venues, organizers, 
              or other integration partners as necessary for providing services.
            </li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-4">2. How We Use Your Information</h2>
          <p className="mb-3">We use your information to:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>
              <strong>Provide and Improve Services:</strong> Facilitate event matching, manage user accounts, 
              and personalize recommendations.
            </li>
            <li>
              <strong>Process Payments:</strong> Handle billing, invoicing, and fraud prevention.
            </li>
            <li>
              <strong>Facilitate Communication:</strong> Send notifications, confirmations, updates, and reminders 
              about events.
            </li>
            <li>
              <strong>Enhance Security:</strong> Detect, prevent, and respond to fraud, abuse, or security incidents.
            </li>
            <li>
              <strong>Safety and Moderation:</strong> Review and moderate user-generated content, including through
              automated scanning, to enforce our Terms and Community Guidelines.
            </li>
            <li>
              <strong>Analytics and Research:</strong> Monitor usage trends, improve our platform, and conduct 
              aggregate research on community engagement.
            </li>
            <li>
              <strong>Legal Compliance:</strong> Comply with applicable laws, regulations, and lawful requests.
            </li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-4">3. Legal Basis for Processing (EEA/UK Users)</h2>
          <p className="mb-3">
            For individuals in the European Economic Area (EEA) or United Kingdom (UK), our processing of 
            personal data is based on:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>Consent (Article 6(1)(a) GDPR)</li>
            <li>Contractual Necessity (Article 6(1)(b) GDPR)</li>
            <li>Legitimate Interests (Article 6(1)(f) GDPR)</li>
            <li>Legal Obligations (Article 6(1)(c) GDPR)</li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-4">4. How We Share Information</h2>
          <p className="mb-3">We do not sell your personal data. We may share information as follows:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>
              <strong>Service Providers:</strong> With trusted vendors such as payment processors, hosting services, 
              analytics providers, and communication platforms.
            </li>
            <li>
              <strong>Event Hosts/Organizers:</strong> Limited data (e.g., name, compatibility markers) necessary
              to facilitate participation.
            </li>
            <li>
              <strong>Other Users and the Public:</strong> When you use community features (such as posting a Kiki,
              RSVPing to an event, or applying to connect with another user), your first name, last initial, profile
              photo, and the content you provide may be visible to other users or publicly on event pages.
            </li>
            <li>
              <strong>Legal and Compliance:</strong> When required by law, regulation, legal process, or 
              governmental request.
            </li>
            <li>
              <strong>Business Transfers:</strong> In connection with a merger, acquisition, restructuring, 
              or sale of assets.
            </li>
            <li>
              <strong>With Consent:</strong> In circumstances where you have explicitly agreed to sharing.
            </li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-4">5. Data Retention</h2>
          <p className="mb-4">
            We retain your information only for as long as necessary to provide our Services, fulfill our legal 
            obligations, resolve disputes, and enforce agreements. Payment information is stored only as required 
            by payment processors.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">6. Security of Your Information</h2>
          <p className="mb-4">
            We implement appropriate technical, organizational, and administrative safeguards to protect personal 
            data against loss, misuse, unauthorized access, disclosure, alteration, and destruction. However, 
            no system is entirely secure, and we cannot guarantee absolute security.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">7. Your Rights</h2>
          <p className="mb-3">Depending on your jurisdiction, you may have rights including:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
            <li><strong>Deletion:</strong> Request deletion of your data, subject to legal obligations.</li>
            <li><strong>Restriction:</strong> Limit how we process your data.</li>
            <li><strong>Portability:</strong> Request transfer of your data to another service.</li>
            <li><strong>Opt-Out:</strong> Withdraw consent for marketing communications at any time.</li>
          </ul>
          <p className="mb-4">To exercise these rights, contact us at the address provided below.</p>

          <h2 className="text-2xl font-bold mt-8 mb-4">8. Children's Privacy</h2>
          <p className="mb-4">
            Our Services are not directed to children under 16. We do not knowingly collect personal information 
            from children. If we become aware that a child under 16 has provided information, we will delete it.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">9. International Transfers</h2>
          <p className="mb-4">
            As a Delaware-based company, we process and store data in the United States and may transfer 
            information to countries with different data protection laws. We implement safeguards such as 
            Standard Contractual Clauses to protect such transfers.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">10. Cookies and Similar Technologies</h2>
          <p className="mb-3">
            We use cookies and similar technologies (local storage, device identifiers) in four categories.
            Only the first is used without your permission.
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>
              <strong>Strictly Necessary:</strong> Required for the Services to function. These cover signing
              in and keeping you signed in (Firebase Authentication), payment and fraud prevention (Stripe),
              and remembering your cookie choice. They cannot be switched off.
            </li>
            <li>
              <strong>Functional:</strong> Remember preferences such as your language, city, and light or dark
              theme, so you do not have to set them on every visit.
            </li>
            <li>
              <strong>Analytics:</strong> Help us understand which pages and features are used, so we can improve
              them. Provided by PostHog and Google Analytics.
            </li>
            <li>
              <strong>Marketing:</strong> Let us measure which advertising brings people to Outclose and credit
              partners who refer new members. Provided by Meta, Google Ads, TikTok, and Rewardful.
            </li>
          </ul>
          <p className="mb-3">
            Where the law requires your prior consent (the European Economic Area, the United Kingdom, and
            Switzerland), nothing outside the strictly necessary category runs until you agree. Elsewhere these
            technologies are enabled by default and you can turn them off at any time. Either way, your choice
            is yours to change whenever you like:
          </p>
          <p className="mb-4">
            <ManageCookiesLink className="text-purple-600 hover:text-purple-700 underline">
              Manage Cookie Preferences
            </ManageCookiesLink>
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">11. Third-Party Links and Services</h2>
          <p className="mb-4">
            Our Services may contain links to third-party websites or integrations. We are not responsible
            for the privacy practices of those third parties.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">12. Changes to This Privacy Policy</h2>
          <p className="mb-4">
            We may update this Privacy Policy from time to time. Updates will be posted with a new "Effective Date." 
            Significant changes will be communicated to users via email or platform notification.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">13. Contact Us</h2>
          <p className="mb-2">
            If you have questions or concerns regarding this Privacy Policy, please contact us at:
          </p>
          <div className="bg-gray-50 dark:bg-neutral-800 p-4 rounded-lg mt-4 mb-8">
            <p className="font-semibold mb-1">GBH Holdings Inc.</p>
            <p className="mb-1">Attn: Privacy Officer</p>
            <p>
              Email: <a href="mailto:team@outclose.com" className="text-purple-600 hover:text-purple-700">
                team@outclose.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

